import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import {
  Button,
  EmptyState,
  EmptyStateProps,
  Modal,
  Pagination,
  RowActionsMenu,
  SkeletonCard,
  TABLE_BODY_SKELETON,
  Tabs,
  TabItem,
  failedEmptyState,
  searchEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { MACRO_COLORS } from "../../mealPlans/macroColors";
// Deep imports rather than the plans barrel: that barrel exports the plan
// pages, and pulling them in here would drag the whole builder behind a tab
// that only lists plans.
import CreatePlanModal from "../../mealPlans/components/CreatePlanModal";
import {
  useCreatedPlans,
  useDeletedPlanIds,
  usePlans,
  useSessionPlans,
} from "../../mealPlans/hooks/usePlans";
import { useMealPlans } from "../../mealPlans/hooks/useMealPlans";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { activateMealPlan } from "../../mealPlans/services/mealPlansService";
import { savePlan } from "../../mealPlans/services/plansService";
import {
  PLAN_STATUS_LABELS,
  PLAN_STATUS_STYLES,
  formatPlanDate,
  toPlanSummary,
} from "../../mealPlans/planMapping";
import { clientDetailPath, planDetailPath } from "../../../routes/paths";
import type { SubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import type {
  NewPlan,
  PlanOrigin,
  PlanSummary,
} from "../../mealPlans/types/plans.types";
import { ClientDetail } from "../types/clients.types";

type MealPlanFilter = "all" | "draft";

/** What one page of the table holds, and what the endpoint is asked for. */
const PLANS_PER_PAGE = 10;

const FILTER_TABS: TabItem<MealPlanFilter>[] = [
  { id: "all", label: "All" },
  { id: "draft", label: "Draft" },
];

/**
 * Each macro column is keyed by a colour chip in its header — the plan
 * module's colours, since this table lists the same macros its own does.
 */
const MACRO_COLUMNS: Array<{ key: keyof typeof MACRO_COLORS; label: string }> = [
  { key: "calories", label: "Calories" },
  { key: "protein", label: "Protein" },
  { key: "carbs", label: "Carbs" },
  { key: "fat", label: "Fat" },
];

const headCell =
  "px-3 sm:px-4 py-3 text-[13px] font-normal text-ink-muted bg-white shadow-[inset_0_-1px_0_#E9EDF1]";
const bodyCell = "px-3 sm:px-4 py-4 text-[14px] text-ink";

export interface ClientMealPlansPanelProps {
  client: ClientDetail;
  /**
   * Whether this workspace may still write plans, read once by the screen above
   * — see `ClientDetailPage`. Meal plans are one of the three things a
   * subscription is what pays for, so with none running the two ways into the
   * builder are off and so is sending a draft. The list itself stays readable.
   */
  gate: SubscriptionGate;
}

/**
 * The Meal half of a client's Plans tab: their meal plans, searched, filtered
 * and paged.
 *
 * Mounted only while Meal is the section on screen — see `ClientPlansTab`. It
 * fetches a page of plans per filter, and a panel left mounted behind the
 * Exercise switch would go on doing that for a list nobody is looking at.
 */
const ClientMealPlansPanel: React.FC<ClientMealPlansPanelProps> = ({
  client,
  gate,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const getToken = useTokenGetter();
  const { create } = usePlans();

  const [filter, setFilter] = useState<MealPlanFilter>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [activating, setActivating] = useState<PlanSummary | null>(null);
  const [activatingNow, setActivatingNow] = useState(false);

  // This client's plans only — the same endpoint the plan list reads, given
  // their `public_id`. The tab, the search box and the page are the server's,
  // so changing any of them refetches.
  const {
    plans: fetched,
    count,
    status: loadStatus,
    error,
  } = useMealPlans({
    client: client.id,
    status: filter === "draft" ? "draft" : "all",
    search,
    page,
    pageSize: PLANS_PER_PAGE,
  });

  // Plans made in this sitting, which no page of the fetched list holds, and
  // plans edited in it, which stand in for the row they were fetched as.
  const createdPlans = useCreatedPlans();
  const sessionPlans = useSessionPlans();
  const deletedIds = useDeletedPlanIds();

  const plans = useMemo(() => {
    const query = search.trim().toLowerCase();

    const editedById = new Map(
      sessionPlans.map(toPlanSummary).map((plan) => [plan.id, plan])
    );
    const gone = new Set(deletedIds);

    // An edited plan replaces the row it was fetched as; only one the server
    // has never seen is new, and only a new plan belongs at the top.
    const remote = fetched
      .filter((plan) => !gone.has(plan.id))
      .map((plan) => editedById.get(plan.id) ?? plan);

    const fetchedIds = new Set(fetched.map((plan) => plan.id));

    // The tab's own filters, applied by hand to the plans made here — this
    // page was fetched before them. On the first page only, since that is
    // where they sit.
    const created =
      page === 1
        ? createdPlans
            .map(toPlanSummary)
            .filter((plan) => !gone.has(plan.id) && !fetchedIds.has(plan.id))
            .filter((plan) => plan.clientId === client.id)
            .filter((plan) => filter === "all" || plan.status === "draft")
            .filter((plan) => !query || plan.name.toLowerCase().includes(query))
        : [];

    return [...created, ...remote];
  }, [
    createdPlans,
    sessionPlans,
    fetched,
    deletedIds,
    client.id,
    filter,
    search,
    page,
  ]);

  // The server's count for the filters in force, not the rows on screen: this
  // table only ever holds one page of them.
  const totalPages = Math.max(1, Math.ceil(count / PLANS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PLANS_PER_PAGE;

  // A filter that shrank the list past the page in hand lands on the last one,
  // rather than leaving the tab asking for a page that is no longer there.
  useEffect(() => {
    if (loadStatus === "ready" && page > totalPages) setPage(totalPages);
  }, [loadStatus, page, totalPages]);

  // A page that arrives quickly goes straight from blank to filled: the
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(loadStatus === "loading");

  // Worked out once rather than per row: every row in the table wears the same
  // answers, and none of these controls is a `Button` — the row and the plan
  // name take the props apart themselves, and the menu has its own way of going
  // quiet. See the entries below.
  const openGate = gate.props("open a plan");
  const activateGate = gate.props("send a plan");

  // A failure comes before emptiness: an empty table under "no meal plans"
  // would read as an answer, when nothing was ever heard back. Nothing about
  // loading — a page on its way says nothing and then shimmers its rows, and
  // the status line below says so in words.
  //
  // A search that matched nothing is its own case, and a loud one here: this
  // tab is about one named person, so "no meal plans" under a search term
  // reads as that client never having been given a plan.
  const emptyState = (): EmptyStateProps => {
    if (loadStatus === "failed") {
      const state = failedEmptyState("We could not load this client’s meal plans");
      return error ? { ...state, description: error } : state;
    }

    if (search.trim()) return searchEmptyState("meal plans");

    if (filter === "draft")
      return {
        title: "No draft plans for this client",
        description:
          "Plans you start for them but do not activate wait here until you finish them.",
      };

    return {
      title: "No meal plans yet",
      description: `Build ${client.name.split(" ")[0]} a plan and it will be listed here with its macros, dates and status.`,
      action: (
        <Button
          size="sm"
          startIcon={<Plus className="w-4 h-4" />}
          onClick={() => setCreating(true)}
          {...gate.props("create a plan")}
        >
          Create plan
        </Button>
      ),
    };
  };

  /**
   * The builder, told where it was opened from — so finishing the plan comes
   * back to this tab rather than leaving for a list the client's screen never
   * mentioned. Shared by creating a plan and by opening an existing one.
   */
  const openPlan = (planId: string) => {
    const origin: PlanOrigin = {
      to: clientDetailPath(client.id, "plans"),
      label: client.name,
    };
    navigate(planDetailPath(planId), { state: { origin } });
  };

  /**
   * The same, reached from the table — or the reason it will not open.
   *
   * Separate from `openPlan` above rather than folded into it, because that one
   * is also how a plan just created is opened, and a plan that has been created
   * is one this workspace was allowed to create.
   */
  const openFromRow = (planId: string) =>
    openGate.disabled ? openGate.onDisabledClick?.() : openPlan(planId);

  /**
   * Sends the plan to the client. The record the backend answers with is
   * written back over the browser's copy, which turns the row's pill from
   * Draft to Active — and marks it as one this sitting has changed, so the
   * row the tab fetched does not overwrite it.
   */
  const handleActivate = async () => {
    if (!activating || activatingNow) return;

    setActivatingNow(true);
    try {
      const record = await activateMealPlan(getToken, activating.id);
      savePlan(record);
      setActivating(null);
      toast.success("Plan activated", `${activating.name} is now with them.`);
    } catch (reason) {
      toast.error(
        "Could not activate the plan",
        reason instanceof ApiError
          ? reason.message
          : "We could not reach the server. Check your connection and try again."
      );
    } finally {
      setActivatingNow(false);
    }
  };

  const handleCreate = async (input: NewPlan) => {
    try {
      const plan = await create(input);
      setCreating(false);
      openPlan(plan.id);
    } catch (reason) {
      // The dialog stays open on its answers, so the create can be tried
      // again without typing them a second time.
      toast.error(
        "Could not create the plan",
        reason instanceof ApiError
          ? reason.message
          : "We could not reach the server. Check your connection and try again."
      );
    }
  };

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <section className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-md border border-line bg-white">
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 sm:px-6 pt-5">
          <h2 className="text-[16px] 3xl:text-[18px] font-medium text-ink">
            Meal plan
          </h2>
          <div className="flex items-center gap-3">
            <div className="relative w-full sm:w-[220px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
              <input
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Search"
                aria-label="Search meal plans"
                className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
              />
            </div>
            <Button
              size="sm"
              startIcon={<Plus className="w-4 h-4" />}
              onClick={() => setCreating(true)}
              {...gate.props("create a plan")}
            >
              Create
            </Button>
          </div>
        </div>

        <div className="flex-shrink-0 px-5 sm:px-6 mt-4 border-b border-line">
          <Tabs
            tabs={FILTER_TABS}
            activeTab={filter}
            onChange={(next) => {
              setFilter(next);
              setPage(1);
            }}
          />
        </div>

        {/* Said as soon as the request goes out, whether or not anything is
            drawn for it: this line is not seen, so it costs nothing to be
            early, and the skeleton rows it stands for are hidden from screen
            readers — they are the shape of an answer rather than the answer. */}
        {loadStatus === "loading" && (
          <p role="status" className="sr-only">
            Loading meal plans…
          </p>
        )}

        {phase !== "ready" && plans.length === 0 ? (
          // One block the size of the table, rather than a page of blocks in
          // the shape of its cells — see `TABLE_BODY_SKELETON`.
          <div className="flex-1 min-h-0 p-4 sm:p-6">
            <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
          </div>
        ) : (
        <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
          {/* Full height only when the rows are gone — see `ClientsTable`. */}
          <table
            className={`w-full min-w-[900px] table-fixed border-collapse text-left ${
              plans.length === 0 ? "h-full" : ""
            }`}
          >
            <colgroup>
              <col className="w-[5%]" />
              <col className="w-[21%]" />
              <col className="w-[12%]" />
              <col className="w-[13%]" />
              <col className="w-[8%]" />
              <col className="w-[8%]" />
              <col className="w-[8%]" />
              <col className="w-[8%]" />
              <col className="w-[12%]" />
              <col className="w-[5%]" />
            </colgroup>
            <thead className="sticky top-0 z-10">
              <tr>
                <th scope="col" className={headCell}>
                  Sl
                </th>
                <th scope="col" className={headCell}>
                  Plan name
                </th>
                <th scope="col" className={headCell}>
                  Meal type
                </th>
                <th scope="col" className={headCell}>
                  Created on
                </th>
                {MACRO_COLUMNS.map((macro) => (
                  <th key={macro.key} scope="col" className={headCell}>
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        aria-hidden="true"
                        className="h-3 w-[3px] rounded-full"
                        style={{ backgroundColor: MACRO_COLORS[macro.key] }}
                      />
                      {macro.label}
                    </span>
                  </th>
                ))}
                <th scope="col" className={headCell}>
                  Status
                </th>
                <th scope="col" className={headCell}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 ? (
                <tr>
                  <td colSpan={10} className="align-middle">
                    <EmptyState {...emptyState()} />
                  </td>
                </tr>
              ) : (
                plans.map((plan, index) => (
                  <tr
                    key={plan.id}
                    onClick={() => openFromRow(plan.id)}
                    // The pointer and the hover tint are the row's whole claim
                    // to being clickable, so both go while it is not — the same
                    // way `PlansTable` draws it.
                    title={openGate.title}
                    className={`border-b border-line last:border-0 transition-colors ${
                      openGate.disabled
                        ? ""
                        : "cursor-pointer hover:bg-[#F8FEFF]"
                    }`}
                  >
                    <td className={bodyCell}>
                      {String(startIndex + index + 1).padStart(2, "0")}
                    </td>
                    <td className={`${bodyCell} font-medium truncate`}>
                      {/* The whole row is clickable for pointers, but the name
                          is a real button so the list stays reachable by
                          keyboard — the same arrangement `PlansTable` uses. */}
                      <button
                        type="button"
                        aria-disabled={openGate.disabled || undefined}
                        title={openGate.title}
                        onClick={(event) => {
                          event.stopPropagation();
                          openFromRow(plan.id);
                        }}
                        // Not dimmed: this is the plan's own name, and greying
                        // it would read as the plan being off rather than the
                        // link. The underline on hover is what offered it, so
                        // that is what goes.
                        className={`max-w-full truncate text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded ${
                          openGate.disabled
                            ? "cursor-not-allowed"
                            : "hover:underline"
                        }`}
                      >
                        {plan.name}
                      </button>
                    </td>
                    <td className={bodyCell}>{plan.planType}</td>
                    <td className={bodyCell}>{formatPlanDate(plan.createdOn)}</td>
                    <td className={bodyCell}>{plan.macros.calories}</td>
                    <td className={bodyCell}>{plan.macros.protein}</td>
                    <td className={bodyCell}>{plan.macros.carbs}</td>
                    <td className={bodyCell}>{plan.macros.fat}</td>
                    <td className={bodyCell}>
                      <span
                        className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
                          PLAN_STATUS_STYLES[plan.status]
                        }`}
                      >
                        {PLAN_STATUS_LABELS[plan.status]}
                      </span>
                    </td>
                    {/* The menu stops its own clicks, so opening it does not
                        also open the plan the row belongs to. */}
                    <td
                      className={bodyCell}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <RowActionsMenu
                        label={`Actions for ${plan.name}`}
                        actions={[
                          {
                            label: "Open plan",
                            disabled: openGate.disabled,
                            onSelect: () => openFromRow(plan.id),
                          },
                          // Only a draft can be sent: one already with the
                          // client has nowhere to go.
                          //
                          // Off with the rest of the writing while there is no
                          // plan behind the workspace — activating is what puts
                          // a plan in the client's hands, which is the thing
                          // being paid for. Greyed rather than dropped, so the
                          // press that comes anyway is answered: an entry that
                          // vanished would read as a draft that cannot be sent
                          // rather than as a workspace that has lapsed.
                          ...(plan.status === "draft"
                            ? [
                                {
                                  label: "Activate plan",
                                  disabled: activateGate.disabled,
                                  onSelect: activateGate.disabled
                                    ? activateGate.onDisabledClick ??
                                      (() => {})
                                    : () => setActivating(plan),
                                },
                              ]
                            : []),
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setPage}
          resultCount={plans.length}
        />
      </section>

      {/* Passed the client, so the dialog opens on the plan's own details: this
          screen is already a client's, and asking which one it is for would be
          asking something the page knows. */}
      <Modal
        open={!!activating}
        title="Activate plan"
        onClose={() => setActivating(null)}
        className="max-w-[440px]"
        footer={
          <>
            <Button
              variant="secondary"
              size="md"
              disabled={activatingNow}
              onClick={() => setActivating(null)}
            >
              Cancel
            </Button>
            <Button size="md" disabled={activatingNow} onClick={handleActivate}>
              {activatingNow ? "Activating…" : "Activate"}
            </Button>
          </>
        }
      >
        <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
          {activating?.name} will go to {client.name} and stop being a draft.
        </p>
      </Modal>

      {creating && (
        <CreatePlanModal
          client={{ id: client.id, name: client.name }}
          onClose={() => setCreating(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
};

export default ClientMealPlansPanel;
